import { Validators } from '@happybandit/react-validation';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import MenuItem from '@material-ui/core/MenuItem';
import useTheme from '@material-ui/core/styles/useTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import AddIcon from '@material-ui/icons/Add';
import { Collections } from '@shared/interfaces/lib/firebaseConstants';
import { isUndefined } from 'lodash';
import React, { useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ContactSelectValid from '../../../../components/ContactSelectValid';
import SelectValid from '../../../../components/SelectValid';
import TextValid from '../../../../components/TextValid';
import { FirebaseContext } from '../../../../firebase_init';
import { INotification, IStrain } from '@shared/interfaces/lib/interfaces';
import {
    selectFacilities,
    selectStrains,
    selectUser,
    selectUsers,
    setError
} from '../../../../redux/appSlice';
import { UserPermissions } from '@shared/interfaces/lib/user';
import { useNotification, uuidv4 } from '../../../../utils';
import { ContactServices } from '../../../externalContacts/interfaces';
import { ITest, ProductTypes } from '../../interfaces';

interface IProps {
}

const NewTest: React.FC<IProps> = () => {
    const context = useContext( FirebaseContext );
    const dispatch = useDispatch();
    const theme = useTheme();
    const fullScreen = useMediaQuery( theme.breakpoints.down( 'sm' ) );
    const [open, setOpen] = useState<boolean>( false );
    const [name, setName] = useState<string>( '' );
    const [unit, setUnit] = useState<string>( '' );
    const [strain, setStrain] = useState<string | undefined>( undefined );
    const [type, setType] = useState<ProductTypes | undefined>( undefined );
    const [deliveree, setDeliveree] = useState<string | undefined>( undefined );
    const [milesDriven, setMilesDriven] = useState<number>( 0 );
    const [dateDelivered, setDateDelivered] = useState<string>( '' );
    const [totalWeight, setTotalWeight] = useState<number>( 0 );
    const [weightDelivered, setWeightDelivered] = useState<number>( 0 );
    const [resultsExpected, setResultsExpected] = useState<string>( '' );
    const [dateDepartedFromFacility, setDateDepartedFromFacility] = useState<string>( '' );
    const [transportVehicle, setVehicleTransport] = useState<string | undefined>( undefined );
    const [testingFacility, setTestingFacility] = useState<string | undefined>( undefined );
    const [originFacility, setOriginFacility] = useState<string | undefined>( undefined );
    const user = useSelector( selectUser );
    const strains = useSelector( selectStrains);
    const users = useSelector( selectUsers );
    const facilities = useSelector( selectFacilities );
    const sendNotification = useNotification();

    const facility = facilities.find( ( facility ) => facility.id === originFacility );
    const vehicles = [
        {
            id: uuidv4(),
            vin: 2374982347298374,
            name: 'Dodge Van',
            make: 'Dodge',
            model: 'E350',
        }
    ];

    const handleClose = () => {
        setUnit( '' );
        setName( '' );
        setStrain( undefined );
        setType( undefined );
        setDeliveree( undefined );
        setMilesDriven( 0 );
        setDateDelivered( '' );
        setTotalWeight( 0 );
        setWeightDelivered( 0 );
        setResultsExpected( '' );
        setTestingFacility( undefined );
        setVehicleTransport( undefined );
        setDateDepartedFromFacility( '' );
        setOpen( false );
    };

    const getTestObject = (): ITest => {
        return {
            unit,
            type: type || ProductTypes.flower,
            name,
            id: uuidv4(),
            strainId: strain || '',
            results: {
                id: '',
                name: '',
                heavyMetals: [],
                potency: {
                    thcCbdRatio: '',
                    cannabinoidPerWeight: 0,
                    totalCannabinoidsPresent: 0,
                },
                terpeneProfile: [{
                    id: '',
                    name: '',
                }],
                chemicalContaminants: [''],
                microbialContaminants: [''],
            },
            testingFacilityId: testingFacility || '',
            facilityOriginatedId: originFacility || '',
            dateDepartedFromFacility: '',
            delivereeId: deliveree || '',
            totalWeight,
            milesDriven,
            dateDelivered,
            inProgress: true,
            weightDelivered,
            resultsExpected,
            transportVehicleId: transportVehicle || '',
        }
    };

    const handleSubmit = () => {
        if ( !user ) {
            return;
        }

        const collection = context.firestore.collection( Collections.tests );
        collection.add( getTestObject() )
            .then( () => {
                handleClose();
            } )
            .catch( ( e: Error ) => {
                dispatch( setError( e.message ) );
            } );

        const notification: Omit<INotification, 'id'> = {
            timestamp: Date.now(),
            seen: false,
            userId: user.uid,
            message: `Created New Test - ${name} in facility ${facility ? facility.metrc.DisplayName : ''}`,
        };

        sendNotification( notification, ( userItem ) => userItem.permissions.includes( UserPermissions.userAdmin ) );
    };

    return (
        <>
            <Button
                onClick={() => setOpen( true )}
                startIcon={<AddIcon/>}
                style={{ float: 'left' }}
                color="primary"
            >
                Add New Test
            </Button>

            <Dialog fullScreen={fullScreen} open={open} onClose={handleClose} aria-labelledby="form-dialog-title">
                <DialogTitle>Create New Test for the Facility</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Tests are required per weight of product depending on type.
                        <br/>
                        <strong>
                            This is serious and extremely important, please ensure and double check all info entered.
                        </strong>
                    </DialogContentText>
                    <SelectValid
                        id="test-strain"
                        label="Strain Being Tested"
                        fullWidth
                        validators={[]}
                        value={strain}
                        onChange={( value ) => setStrain( value )}
                    >
                        <MenuItem value=""/>
                        {strains.map( ( strain: IStrain ) => (
                            <MenuItem
                                key={strain.id}
                                value={strain.id}
                            >
                                {strain.name}
                            </MenuItem>
                        ) )}
                    </SelectValid>
                    <SelectValid
                        id="test-type"
                        label="Type of Product Being Tested"
                        fullWidth
                        validators={[]}
                        value={!isUndefined( type ) ? type : ''}
                        onChange={( value ) => setType( ProductTypes[value as keyof typeof ProductTypes] )}
                    >
                        <MenuItem value=""/>
                        {Object.keys( ProductTypes ).map( ( type ) => (
                            <MenuItem
                                key={type}
                                value={type}
                            >
                                {type}
                            </MenuItem>
                        ) )}
                    </SelectValid>
                    <ContactSelectValid
                        id="test-facility"
                        label="Facility Testing At"
                        fullWidth
                        validators={[]}
                        value={testingFacility}
                        onChange={( value ) => setTestingFacility( value )}
                        serviceToFocus={ContactServices.ProductTesting}
                    />
                    <SelectValid
                        id="test-origin-facility"
                        label="Facility Sent From"
                        fullWidth
                        validators={[Validators.required( originFacility || '' )]}
                        value={originFacility}
                        onChange={( value ) => setOriginFacility( value )}
                    >
                        <MenuItem value=""/>
                        {facilities.map( ( facility ) => (
                            <MenuItem
                                key={facility.id}
                                value={facility.id}
                            >
                                {facility.metrc.DisplayName}
                            </MenuItem>
                        ) )}
                    </SelectValid>
                    <SelectValid
                        id="test-employee"
                        label="Employee Delivering the Test"
                        fullWidth
                        validators={[]}
                        value={deliveree}
                        onChange={( value ) => setDeliveree( value )}
                    >
                        <MenuItem value=""/>
                        {users.map( ( user ) => (
                            <MenuItem
                                key={user.uid}
                                value={user.uid}
                            >
                                {user.displayName}
                            </MenuItem>
                        ) )}
                    </SelectValid>
                    <SelectValid
                        id="test-vehicle"
                        label="Vehicle Used to Deliver the Test"
                        fullWidth
                        validators={[]}
                        value={transportVehicle}
                        onChange={( value ) => setVehicleTransport( value )}
                    >
                        <MenuItem value=""/>
                        {vehicles.map( ( vehicle ) => (
                            <MenuItem
                                key={vehicle.id}
                                value={vehicle.id}
                            >
                                {vehicle.name}
                            </MenuItem>
                        ) )}
                    </SelectValid>
                    <TextValid
                        id="test-new-name"
                        label="Name"
                        autoComplete='none'
                        value={name}
                        onChange={( value ) => setName( value )}
                        fullWidth
                        validators={[Validators.required( name )]}
                    />
                    <TextValid
                        id="test-new-unit"
                        label="Unit"
                        autoComplete='none'
                        value={unit}
                        onChange={( value ) => setUnit( value )}
                        fullWidth
                        validators={[Validators.required( unit )]}
                    />
                    <TextValid
                        type="number"
                        autoComplete='none'
                        label="Miles Driven"
                        id="test-miles-driven"
                        value={milesDriven.toString()}
                        onChange={( value ) => setMilesDriven( parseInt( value ) )}
                        fullWidth
                        validators={[Validators.required( milesDriven.toString() )]}
                    />
                    <TextValid
                        id="test-total-weight-sent"
                        label="Total Weight Sent ( grams )"
                        autoComplete='none'
                        value={totalWeight.toString()}
                        onChange={( value ) => setTotalWeight( parseInt( value ) )}
                        fullWidth
                        validators={[Validators.required( totalWeight.toString() )]}
                    />
                    <TextValid
                        id="test-weight-delivered"
                        label="Total Weight Delivered"
                        autoComplete='none'
                        value={weightDelivered.toString()}
                        onChange={( value ) => setWeightDelivered( parseInt( value ) )}
                        fullWidth
                        validators={[Validators.required( weightDelivered.toString() )]}
                    />
                    <TextValid
                        id="test-date-departed"
                        label="Date Departed From Facility"
                        autoComplete='none'
                        value={dateDepartedFromFacility}
                        onChange={( value ) => setDateDepartedFromFacility( value )}
                        fullWidth
                        validators={[Validators.required( dateDepartedFromFacility )]}
                    />
                    <TextValid
                        id="test-results-expected"
                        label="Date Results Expected"
                        autoComplete='none'
                        value={resultsExpected}
                        onChange={( value ) => setResultsExpected( value )}
                        fullWidth
                        validators={[Validators.required( resultsExpected )]}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleSubmit} color="primary">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default NewTest;